import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import type { JobAcceptedResponse, MeetingActivityResponse, MeetingDetail, ProcessMeetingResponse, ProjectListResponse, SetMeetingProjectsRequest, TranscriptResponse } from '../../shared/api-types.js';
import { api } from '../api.js';
import { PATHS } from '../paths.js';
import { useMe } from '../shell/me-context.js';
import { RegionErrorBoundary } from '../shell/RegionErrorBoundary.js';
import {
  Button,
  ConfirmDialog,
  CopyButton,
  EM_DASH,
  FailureView,
  IconButton,
  LinkButton,
  Page,
  PageHead,
  RelativeTime,
  Spinner,
  StateView,
  StatusPill,
  TabPanel,
  Tabs,
  Icon,
  failureDetail,
  formatCount,
  formatListWithRest,
  formatDate,
  formatDateTime,
  formatDuration,
  pluralise,
  useRequest,
  useIsMobile,
  useToast,
  useUrlTab,
  type Crumb,
} from '../ui/index.js';
import { showsSlackTab, slackAnalysisFloorMet, slackContextView, slackProgress, slackTabCount } from './lib/slack-context.js';
import { awaitsProcessing, summaryInFlight, summaryPresentation, type SummaryState } from './lib/summary.js';
import { REGENERATE_PROJECTS_SENTENCE, filedUnderText, parseTodoFilters, projectsFiledByNote, regenerateFellowLine, writeTodoFilters, type ProjectFilterOption, type TodoFilters } from './lib/todos.js';
import { useLazyRequest } from './lib/use-lazy-request.js';
import { MeetingProjectsDialog } from './meeting-projects-dialog.js';
import { ActivityTab, SummaryTab, TranscriptTab, languageName } from './meeting-tabs.js';
import { OWN_PROJECTS_PATH } from './project-checkboxes.js';
import { ProjectChipList, projectNames } from './project-filing.js';
import { LookInSlackDialog, SlackContextTab, useSlackContext } from './slack-context-tab.js';
import { AddTodoDialog, TodoFilterBar, TodoTable, useTodoList } from './todo-table.js';
import './pages.css';

// The meeting record page (design system 06-modules §5, in full). R1 — the identity header — is
// never lazy: kind, title, state, the quotable Fellow recording id, source and last sync, and the
// primary act. R2 is the field list. R3 are the tabs (to-dos, transcript, Slack), R4 the activity
// feed; the tab, the embedded table's sort/filters, a highlighted to-do (?todo=), a transcript segment
// (#t-<index>) and a Slack finding (#s-<id>) are all in the address (§5.5). Identity stays evident
// while scrolling through a condensed header at --z-sticky.

const TABS = ['summary', 'todos', 'transcript', 'slack', 'activity'] as const;
type TabId = (typeof TABS)[number];

const TODOS_PREFIX = 'todos_';
const TODO_HIGHLIGHT_PARAM = 'todo';
const POLL_INTERVAL_MS = 5_000;
/**
 * Twenty minutes of polling before the page stops and offers a manual check. Why twenty (was eight):
 * the slowest honest path is three Anthropic attempts of up to 300 s each (the summariser's timeout
 * since the calibration of 2026-09-27) with the queue's 30 s and 60 s back-offs between them
 * (16.5 min); anything longer is worth telling the user about.
 */
const POLL_MAX = 240;

const CRUMBS: readonly Crumb[] = [{ label: 'Meetings', to: PATHS.meetings, role: 'module' }];

export default function MeetingPage() {
  const { id = '' } = useParams<{ id: string }>();
  const me = useMe();
  const toast = useToast();
  const isMobile = useIsMobile();
  const [params, setParams] = useSearchParams();
  const [urlTab, setTab] = useUrlTab<TabId>(TABS, 'summary');
  const encodedId = encodeURIComponent(id);

  const detail = useRequest<MeetingDetail>((signal) => api.get<MeetingDetail>(`/api/meetings/${encodedId}`, { signal }), [encodedId]);
  const meeting = detail.data;
  // The Slack tab is not there while Slack checks cannot exist here (owner ruling D8; design-deviations.md
  // §2); a link to it then lands on the summary, as any unknown tab does.
  const slackShown = meeting !== null && showsSlackTab(me.integrations, meeting.slackContext);
  const tab: TabId = urlTab === 'slack' && !slackShown ? 'summary' : urlTab;

  // R3/R4 load when their tab is first opened (or deep-linked) and then stay loaded.
  const [transcriptWanted, setTranscriptWanted] = useState(false);
  const [activityWanted, setActivityWanted] = useState(false);
  useEffect(() => {
    if (tab === 'transcript' || tab === 'todos') setTranscriptWanted(true);
    if (tab === 'activity') setActivityWanted(true);
  }, [tab]);
  const transcript = useLazyRequest<TranscriptResponse>(
    (signal) => api.get<TranscriptResponse>(`/api/meetings/${encodedId}/transcript`, { signal }),
    [encodedId],
    transcriptWanted && meeting !== null && meeting.transcriptStatus === 'available',
  );
  const activity = useLazyRequest<MeetingActivityResponse>(
    (signal) => api.get<MeetingActivityResponse>(`/api/meetings/${encodedId}/activity`, { signal }),
    [encodedId],
    activityWanted && meeting !== null,
  );
  // A finished Slack check changes what the page says about it (the tab's count, the Details row). Why
  // it is read as soon as the tab is shown, not first opened (owner ruling A11b): the head's "Regenerate
  // Slack analysis" needs the check's state and whether one may be asked for now, before any tab is
  // opened — the same answer the tab shows, read once and shared.
  const slack = useSlackContext(id, slackShown, detail.reload);

  // --- Regenerating the Slack analysis from the head (owner ruling A11b) -------------------------------
  const [slackDialogOpen, setSlackDialogOpen] = useState(false);
  // Why a count of openings: as on the tab, each opening is a fresh dialog with its own preview.
  const [slackDialogOpening, setSlackDialogOpening] = useState(0);
  // Why: between the request being accepted and the tab's next read landing, the head would offer the
  // button again for a moment; the flag holds the pending state until that read arrives, whatever it says.
  const [awaitingSlackCheck, setAwaitingSlackCheck] = useState(false);
  useEffect(() => {
    setAwaitingSlackCheck(false);
  }, [slack.request.data]);

  // The embedded to-do table: its filters live in the URL under a prefix so they cannot collide
  // with the page's own params (06-modules §5.5).
  const todoFilters = useMemo(() => parseTodoFilters(params, TODOS_PREFIX), [params]);
  const setTodoFilters = useCallback((next: TodoFilters) => {
    setParams((current) => writeTodoFilters(current, next, TODOS_PREFIX), { replace: true });
  }, [setParams]);
  const todos = useTodoList(todoFilters, { prefix: TODOS_PREFIX, fixedMeetingId: id });
  const [addOpen, setAddOpen] = useState(false);

  // --- Projects (ADR 0017; owner rulings of 2026-10-02) -------------------------------------------------
  // The R2 row and its "Change projects" exist only under the projects floor (ruling 9; selection only — the route asks
  // the floor itself). The person's own live projects feed the embedded table's Project filter as well.
  const projectsOn = me.integrations.projects === 'on';
  const [projectsOpen, setProjectsOpen] = useState(false);
  const ownProjects = useRequest<ProjectListResponse | null>(
    (signal) => (projectsOn ? api.get<ProjectListResponse>(OWN_PROJECTS_PATH, { signal }) : Promise.resolve({ ok: true as const, status: 200, data: null })),
    [projectsOn],
  );
  const projectOptions: ProjectFilterOption[] | null = useMemo(
    () => (ownProjects.data ? ownProjects.data.items.map((project) => ({ id: project.id, name: project.name, slot: project.slot })) : null),
    [ownProjects.data],
  );
  // A filing is the person's sorting (ruling 2): the record takes the answer and the toast offers Undo, which sends the
  // previous set back through the same route (03-components §4.2 prefers undo to confirm).
  const announceProjects = (saved: MeetingDetail, previous: string[]): void => {
    toast.success(filedUnderText(projectNames(saved.projects)), {
      key: `meeting-projects-${saved.id}`,
      action: {
        label: 'Undo',
        onClick: () => {
          void api.patch<MeetingDetail>(`/api/meetings/${encodedId}/projects`, { projectIds: previous } satisfies SetMeetingProjectsRequest).then((result) => {
            if (!result.ok) {
              toast.error(<>We couldn&rsquo;t undo that filing. {failureDetail(result)}</>);
              return;
            }
            detail.setData(result.data);
          });
        },
      },
    });
  };

  // --- Summary generation and polling ---------------------------------------------------------
  const [requesting, setRequesting] = useState(false);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  // Why: a regeneration queued behind a finished summary is invisible in GET /api/meetings/:id
  // until the worker starts it (the finished row is kept). Remembering which summary we asked to
  // replace lets the page show "pending" honestly until the API catches up.
  const [regenerationOf, setRegenerationOf] = useState<string | null>(null);
  const [polls, setPolls] = useState(0);
  const previousSummaryId = useRef<string | null>(null);

  const apiState: SummaryState = meeting?.summary?.status ?? 'none';
  const regenerationPending = regenerationOf !== null && (meeting?.summary?.id ?? 'none') === regenerationOf && !summaryInFlight(meeting?.summary ?? null);
  const state: SummaryState = regenerationPending ? 'queued' : apiState;
  const inFlight = state === 'queued' || state === 'running';

  useEffect(() => {
    if (!meeting) return;
    const currentId = meeting.summary?.id ?? 'none';
    if (regenerationOf !== null && (currentId !== regenerationOf || summaryInFlight(meeting.summary))) setRegenerationOf(null);
    // A new finished summary replaces the to-dos: refresh the embedded table and its count.
    if (previousSummaryId.current !== null && previousSummaryId.current !== currentId && meeting.summary?.status === 'ready') {
      todos.request.reload();
    }
    previousSummaryId.current = currentId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting?.summary?.id, meeting?.summary?.status]);

  useEffect(() => {
    if (!inFlight || polls >= POLL_MAX) return;
    const handle = window.setTimeout(() => {
      setPolls((count) => count + 1);
      detail.reload();
    }, POLL_INTERVAL_MS);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inFlight, polls, meeting?.summary?.id, meeting?.summary?.status]);

  const requestSummary = async (): Promise<void> => {
    if (!meeting) return;
    setRequesting(true);
    const result = await api.post<JobAcceptedResponse>(`/api/meetings/${encodedId}/summarise`);
    setRequesting(false);
    setConfirmRegenerate(false);
    if (!result.ok) {
      toast.error(<>We couldn&rsquo;t request the summary. {failureDetail(result)}</>);
      return;
    }
    toast.success(result.data.alreadyQueued ? 'A summary is already on its way.' : 'Summary requested — it will appear here in a minute or so.');
    setRegenerationOf(meeting.summary?.id ?? 'none');
    setPolls(0);
  };

  const onGenerate = (): void => {
    if (meeting?.summary?.status === 'ready') setConfirmRegenerate(true);
    else void requestSummary();
  };

  // --- Processing a meeting the importer left alone (spec §10) ----------------------------------
  // Why a separate request: the meeting was imported outside the automatic window, so nothing has
  // been started on it. Processing queues the summary (then the same polling as above) or, for an
  // account without AI summaries, imports Fellow's own action items here and now.
  const [processing, setProcessing] = useState(false);
  const processMeeting = async (): Promise<void> => {
    if (!meeting) return;
    setProcessing(true);
    const result = await api.post<ProcessMeetingResponse>(`/api/meetings/${encodedId}/process`);
    setProcessing(false);
    if (!result.ok) {
      toast.error(<>We couldn&rsquo;t process the meeting. {failureDetail(result)}</>);
      // A 409 means the state moved on (processed elsewhere): show the current record.
      if (result.code === 'conflict') detail.reload();
      return;
    }
    if (result.data.jobId !== null) {
      toast.success(result.data.alreadyQueued ? 'A summary is already on its way.' : 'Processing requested — the summary will appear here in a minute or so.');
      setRegenerationOf(meeting.summary?.id ?? 'none');
      setPolls(0);
    } else {
      toast.success(`Processed — ${pluralise(result.data.fellowTodosImported, { one: '# to-do', other: '# to-dos' })} imported from Fellow’s notes.`);
      todos.request.reload();
    }
    detail.reload();
  };

  // --- Condensed identity while scrolling ------------------------------------------------------
  const headRef = useRef<HTMLDivElement | null>(null);
  const [condensed, setCondensed] = useState(false);
  useEffect(() => {
    const head = headRef.current;
    if (!head || !meeting) return;
    let frame = 0;
    const update = (): void => {
      frame = 0;
      const bar = document.querySelector<HTMLElement>('.top-bar');
      const barBottom = bar ? bar.getBoundingClientRect().bottom : 0;
      setCondensed(head.getBoundingClientRect().bottom < barBottom);
    };
    const schedule = (): void => {
      if (frame === 0) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (frame !== 0) cancelAnimationFrame(frame);
    };
  }, [meeting]);

  // --- Page states --------------------------------------------------------------------------------

  if (detail.status === 'loading' && !meeting) {
    return (
      <Page measure="read-wide" className="record-page">
        <PageHead title="Meeting" crumbs={CRUMBS} />
        <StateView kind="loading" label="Loading the meeting" minHeight="40dvh" />
      </Page>
    );
  }

  if (!meeting) {
    const failure = detail.error;
    return (
      <Page measure="read-wide" className="record-page">
        <PageHead title="Meeting" crumbs={CRUMBS} />
        {failure && failure.code === 'not_found' ? (
          <StateView
            kind="not-found"
            title="Meeting not found"
            description={
              <>
                There is no meeting with the id <code className="selectable">{id}</code> in your account. It may have been removed, or the link may be out of date.
              </>
            }
            action={<LinkButton to={PATHS.meetings} variant="secondary">Go to meetings</LinkButton>}
            minHeight="40dvh"
          />
        ) : failure ? (
          <FailureView failure={failure} what="this meeting" onRetry={detail.reload} backTo={{ to: PATHS.meetings, label: 'Go to meetings' }} minHeight="40dvh" />
        ) : (
          <StateView kind="loading" label="Loading the meeting" minHeight="40dvh" />
        )}
      </Page>
    );
  }

  const presentation = summaryPresentation(state, meeting.transcriptStatus, meeting.processedAt);
  const pill = <StatusPill tone={presentation.tone} icon={presentation.icon} title={presentation.detail}>{presentation.word}</StatusPill>;
  const canGenerate = me.capabilities.aiSummaries && meeting.transcriptStatus === 'available';
  // Not yet processed and nothing in flight: the primary act is to process it (any owner may).
  const awaiting = awaitsProcessing(meeting) && !inFlight;
  const durationSeconds = meeting.startedAt && meeting.endedAt ? Math.max(0, (Date.parse(meeting.endedAt) - Date.parse(meeting.startedAt)) / 1000) : null;
  const attendeeNames = meeting.attendees.map((attendee) => attendee.name ?? attendee.email).filter((value): value is string => Boolean(value));

  const primaryAction = awaiting ? (
    <Button variant="primary" icon="play" onClick={() => void processMeeting()} loading={processing}>Process meeting</Button>
  ) : canGenerate ? (
    <Button variant="primary" icon="sparkles" onClick={onGenerate} loading={requesting || inFlight} aria-describedby={inFlight ? 'summary-inflight-note' : undefined}>
      {inFlight ? 'Generating summary' : meeting.summary?.status === 'ready' ? 'Regenerate summary' : 'Generate summary'}
    </Button>
  ) : null;

  // The secondary act (owner ruling A11b): beside the summary's button when the Slack tab is shown and a
  // summary is ready. It opens the very dialog the tab's "Look in Slack again…" opens — the per-run
  // consent — and sends the same request. While a check waits or runs its state stands in the button's
  // place, and where a check cannot be asked for now the reason does (03-components §2: no disabled
  // button as the only explanation).
  const slackData = slack.request.data;
  const slackView = slackShown && slackData ? slackContextView(slackData, { floorMet: slackAnalysisFloorMet(me.integrations) }) : null;
  const slackProgressNow = slackData && (slackView?.state === 'pending' || slackView?.state === 'running') ? slackProgress(slackData) : null;
  const slackAction = (() => {
    if (!slackView || !slackData || meeting.summary?.status !== 'ready') return null;
    if (slackProgressNow || awaitingSlackCheck) {
      const word = slackProgressNow?.word ?? 'Pending';
      const text = slackProgressNow?.text ?? 'Waiting to look in Slack';
      // Why the spinner carries the words: it is the status region (03-components §12), so the state is
      // announced once; the visible text beside it says the same and is decorative to a screen reader.
      return (
        <span className="slack-head-status text-sm secondary">
          <Spinner size={16} label={`${word} · ${text}`} />
          <span aria-hidden="true"><strong>{word}</strong> · {text}…</span>
        </span>
      );
    }
    if (!slackView.request) return null;
    if (slackView.request.blockedReason !== null) return <p className="slack-head-status text-sm muted">{slackView.request.blockedReason}</p>;
    return (
      <Button
        variant="secondary"
        icon="search"
        onClick={() => {
          setSlackDialogOpening((count) => count + 1);
          setSlackDialogOpen(true);
        }}
      >
        {slackView.state === 'never' ? 'Run Slack analysis' : 'Regenerate Slack analysis'}
      </Button>
    );
  })();
  const headActions = primaryAction || slackAction ? (
    <>
      {primaryAction}
      {slackAction}
    </>
  ) : null;

  const tabs = [
    { id: 'summary' as const, label: 'Summary' },
    { id: 'todos' as const, label: 'To-dos', count: meeting.todoCounts.open },
    { id: 'transcript' as const, label: 'Transcript', count: meeting.segmentCount },
    ...(slackShown ? [{ id: 'slack' as const, label: 'Slack', count: slackTabCount(meeting.slackContext, slack.request.data) }] : []),
    { id: 'activity' as const, label: 'Activity', count: activity.data?.items.length },
  ];
  const slackInFlight = meeting.slackContext?.status === 'queued' || meeting.slackContext?.status === 'running';

  return (
    <Page measure="read-wide" className="record-page">
      <div ref={headRef}>
        <PageHead
          title={meeting.title}
          crumbs={CRUMBS}
          titleAside={pill}
          identifier={
            <span className="record-identifier">
              <span>Fellow recording</span>
              <code className="selectable">{meeting.fellowRecordingId}</code>
              <CopyButton value={meeting.fellowRecordingId} label="Copy id" />
            </span>
          }
          subtitle={
            <>
              Last synced <RelativeTime value={meeting.source.lastSyncedAt} />
              {detail.refreshing ? <span className="visually-hidden"> (checking for changes)</span> : null}
            </>
          }
          actions={headActions}
        />
      </div>
      {inFlight ? <p id="summary-inflight-note" className="visually-hidden">A summary is being generated; the button is unavailable until it finishes.</p> : null}

      <div className="record-sticky" hidden={!condensed} aria-hidden="true">
        <span className="record-sticky__kind">Meeting</span>
        <span className="record-sticky__title">{meeting.title}</span>
        <span className="record-sticky__pill">{pill}</span>
      </div>

      {/* R2. On a phone the attributes collapse behind a one-line digest so the record's own
          content (the tabs) begins within the first viewport (06-modules §5.10); the identifying
          fields stay in the header above, never behind the disclosure. */}
      <details className="disclosure record-details" open={!isMobile} key={isMobile ? 'mobile' : 'desktop'}>
        <summary>
          <Icon name="chevron-down" size={16} />
          <span className="record-details__label">Details</span>
          <span className="record-details__digest text-sm secondary num">
            {meeting.startedAt ? formatDate(meeting.startedAt) : EM_DASH}
            {durationSeconds !== null ? ` · ${formatDuration(durationSeconds)}` : ''}
            {` · ${pluralise(meeting.attendees.length, { one: '# attendee', other: '# attendees' })}`}
            {/* The first project by name (design §7.7); the row below carries them all. */}
            {projectsOn && meeting.projects[0] ? ` · ${meeting.projects[0].name}` : ''}
          </span>
        </summary>
        <div className="disclosure__body">
          <dl className="field-list">
            <dt>When</dt>
            <dd>{meeting.startedAt ? <time dateTime={meeting.startedAt}>{formatDateTime(meeting.startedAt)}</time> : EM_DASH}</dd>
            <dt>Duration</dt>
            <dd>{durationSeconds !== null ? formatDuration(durationSeconds) : EM_DASH}</dd>
            <dt>Attendees</dt>
            <dd>
              {attendeeNames.length > 0
                ? formatListWithRest(attendeeNames, meeting.attendees.length - attendeeNames.length, { one: '# unnamed attendee', other: '# unnamed attendees' })
                : meeting.attendees.length > 0 ? pluralise(meeting.attendees.length, { one: '# attendee (unnamed)', other: '# attendees (unnamed)' }) : EM_DASH}
            </dd>
            {projectsOn ? (
              <>
                <dt>Projects</dt>
                {/* Editable, and evidently so (06-modules §5.3 R2): the chips, who filed them, and the one control that changes them. */}
                <dd className="meeting-projects cluster">
                  <ProjectChipList projects={meeting.projects} />
                  {projectsFiledByNote(meeting.projects) ? <span className="text-xs muted">{projectsFiledByNote(meeting.projects)}</span> : null}
                  <IconButton icon="edit" label="Change projects" size="sm" onClick={() => setProjectsOpen(true)} />
                </dd>
              </>
            ) : null}
            <dt>Language</dt>
            <dd>{meeting.languageCode ? languageName(meeting.languageCode) : EM_DASH}</dd>
            <dt>Imported</dt>
            <dd><RelativeTime value={meeting.source.importedAt} mode="both" /></dd>
            <dt>Fellow note id</dt>
            <dd>{meeting.fellowNoteId ? <code className="selectable">{meeting.fellowNoteId}</code> : EM_DASH}</dd>
            {slackShown ? (
              <>
                <dt>Slack check</dt>
                <dd>
                  {meeting.slackContext?.checkedAt
                    ? <>Checked <time dateTime={meeting.slackContext.checkedAt}>{formatDateTime(meeting.slackContext.checkedAt)}</time></>
                    : slackInFlight ? 'Checking now' : EM_DASH}
                </dd>
              </>
            ) : null}
          </dl>
        </div>
      </details>

      <div className="record-tabs">
        <Tabs<TabId> id="meeting" label="Meeting sections" tabs={tabs} value={tab} onChange={setTab} />

        <TabPanel tabsId="meeting" tabId="summary" active={tab === 'summary'} heading="Summary">
          <RegionErrorBoundary region="the summary" compact>
            <SummaryTab
              meeting={meeting}
              state={state}
              aiProvider={me.integrations.ai}
              canGenerate={canGenerate}
              requesting={requesting}
              onGenerate={onGenerate}
              awaitingProcessing={awaiting}
              processing={processing}
              onProcess={() => void processMeeting()}
              pollingExhausted={inFlight && polls >= POLL_MAX}
              onCheckAgain={() => { setPolls(0); detail.reload(); }}
            />
          </RegionErrorBoundary>
        </TabPanel>

        <TabPanel tabsId="meeting" tabId="todos" active={tab === 'todos'} heading="To-dos">
          <RegionErrorBoundary region="the to-dos" compact>
            <div className="stack">
              <div className="split">
                <p className="text-sm secondary">
                  {formatCount(meeting.todoCounts.open)} open · {formatCount(meeting.todoCounts.done)} completed · {formatCount(meeting.todoCounts.dismissed)} dismissed
                </p>
                <Button variant="secondary" icon="plus" size="sm" onClick={() => setAddOpen(true)}>Add a to-do</Button>
              </div>
              <TodoFilterBar
                filters={todoFilters}
                onChange={setTodoFilters}
                hideMeetingFilter
                projectOptions={projectOptions}
                projectOptionsFailure={ownProjects.status === 'error' ? ownProjects.error : null}
                onRetryProjectOptions={ownProjects.reload}
                list={todos}
                counts={todos.request.data ? { matching: todos.request.data.matching, total: meeting.todoCounts.open + meeting.todoCounts.done + meeting.todoCounts.dismissed } : null}
              />
              <TodoTable
                caption="To-dos for this meeting"
                list={todos}
                filters={todoFilters}
                onFiltersChange={setTodoFilters}
                prefix={TODOS_PREFIX}
                highlightParam={TODO_HIGHLIGHT_PARAM}
                fixedMeetingId={id}
                transcriptSegments={transcript.data?.segments ?? null}
                totalInSet={meeting.todoCounts.open + meeting.todoCounts.done + meeting.todoCounts.dismissed}
                onMutated={detail.reload}
                empty={{
                  title: 'No to-dos for this meeting',
                  description: meeting.transcriptStatus === 'empty'
                    ? 'This recording has no transcript, so no to-dos could be drawn from it.'
                    : awaitsProcessing(meeting)
                      ? 'This meeting has not been processed yet. Process it to draw to-dos from the transcript.'
                      : meeting.summary?.status === 'ready'
                      ? 'The summary found no follow-up items.'
                      : 'To-dos are drawn from the transcript when the summary is generated.',
                  action: <Button variant="secondary" icon="plus" onClick={() => setAddOpen(true)}>Add a to-do</Button>,
                }}
              />
            </div>
          </RegionErrorBoundary>
        </TabPanel>

        <TabPanel tabsId="meeting" tabId="transcript" active={tab === 'transcript'} heading="Transcript">
          <RegionErrorBoundary region="the transcript" compact>
            <TranscriptTab request={transcript} transcriptStatus={meeting.transcriptStatus} />
          </RegionErrorBoundary>
        </TabPanel>

        {slackShown ? (
          <TabPanel tabsId="meeting" tabId="slack" active={tab === 'slack'} heading="Slack">
            <RegionErrorBoundary region="the Slack check" compact>
              <SlackContextTab
                meeting={meeting}
                context={slack}
                floorMet={slackAnalysisFloorMet(me.integrations)}
                ai={me.integrations.ai}
                canChooseChannels={me.capabilities.slackConnect}
                onChanged={detail.reload}
                onTodoAdded={() => {
                  todos.request.reload();
                  detail.reload();
                }}
                onTodoChanged={() => {
                  todos.request.reload();
                  detail.reload();
                }}
              />
            </RegionErrorBoundary>
          </TabPanel>
        ) : null}

        <TabPanel tabsId="meeting" tabId="activity" active={tab === 'activity'} heading="Activity">
          <RegionErrorBoundary region="the activity" compact>
            <ActivityTab request={activity} />
          </RegionErrorBoundary>
        </TabPanel>
      </div>

      <AddTodoDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        fixedMeetingId={id}
        onCreated={(created) => {
          setAddOpen(false);
          toast.success('To-do added');
          setParams((current) => {
            const next = writeTodoFilters(current, { ...todoFilters, q: '' }, TODOS_PREFIX);
            next.set(TODO_HIGHLIGHT_PARAM, created.id);
            return next;
          }, { replace: true });
          todos.request.reload();
          detail.reload();
        }}
      />

      {projectsOn ? (
        <MeetingProjectsDialog
          meeting={meeting}
          open={projectsOpen}
          onClose={() => setProjectsOpen(false)}
          onSaved={(saved, change) => {
            setProjectsOpen(false);
            detail.setData(saved);
            announceProjects(saved, change.previous);
          }}
        />
      ) : null}

      {slackShown ? (
        <LookInSlackDialog
          key={`head-${slackDialogOpening}`}
          open={slackDialogOpen}
          meeting={meeting}
          ai={me.integrations.ai}
          climate={me.integrations.executive === 'on'}
          onClose={() => setSlackDialogOpen(false)}
          onQueued={(accepted) => {
            setSlackDialogOpen(false);
            setAwaitingSlackCheck(true);
            toast.success(
              accepted.alreadyQueued
                ? 'Calis is already looking in Slack for this meeting.'
                : 'Calis is looking in Slack. What it finds appears on the Slack tab.',
            );
            // The result lands on the Slack tab; the page goes there so the person sees it arrive.
            setTab('slack');
            slack.follow();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={confirmRegenerate}
        title="Replace the summary?"
        confirmLabel="Regenerate"
        busy={requesting}
        onCancel={() => { if (!requesting) setConfirmRegenerate(false); }}
        onConfirm={requestSummary}
      >
        <p>
          Calis will replace the summary, key points and decisions for <strong>{meeting.title}</strong>, and replace any AI to-dos you have not changed. To-dos you have changed — edited, completed, dismissed or unlinked — are kept.
        </p>
        {/* Import or tie (Stage 2): said only where Calis ties Fellow's items (FELLOW_MATCH_MODE) and the person connects Fellow. */}
        {regenerateFellowLine(me) ? <p>{regenerateFellowLine(me)}</p> : null}
        {/* Projects (ADR 0017): said only under the floor — with it off nothing is sorted, and the sentence would be false. */}
        {projectsOn ? <p>{REGENERATE_PROJECTS_SENTENCE}</p> : null}
      </ConfirmDialog>
    </Page>
  );
}
